import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Send, Sparkles, Save } from "lucide-react";
import { campaignAPI } from "../services/api";

function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
}

const CampaignDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [recipients, setRecipients] = useState<any[]>([]);
  const [insights, setInsights] = useState<any[]>([]);
  const [audiencePreview, setAudiencePreview] = useState<{
    eligible: number;
    sample: { name: string; room_number: string }[];
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [scheduleLocal, setScheduleLocal] = useState("");
  const [titleEdit, setTitleEdit] = useState("");
  const [bodyEdit, setBodyEdit] = useState("");
  const [draftGuests, setDraftGuests] = useState<{ id: string; name: string; room_number: string }[]>(
    []
  );
  const [selectedGuestIds, setSelectedGuestIds] = useState<Set<string>>(new Set());
  const [audienceDirty, setAudienceDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const scheduleInitForDraft = useRef(false);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    scheduleInitForDraft.current = false;
  }, [id]);

  const load = async () => {
    if (!id) return;
    setLoadError(null);
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        campaignAPI.get(id),
        campaignAPI.recipients(id),
        campaignAPI.insights(id),
      ]);
      const [cRes, rRes, insRes] = results;
      if (cRes.status === "rejected") {
        console.error(cRes.reason);
        setLoadError("Could not load campaign (try again).");
        setLoading(false);
        return;
      }
      const payload = cRes.value as {
        campaign: any;
        stats: any;
        audience_preview?: { eligible: number; sample: { name: string; room_number: string }[] };
      };
      setData(payload);
      setAudiencePreview(payload.audience_preview ?? null);
      const c = payload.campaign;
      setTitleEdit(c.title ?? "");
      setBodyEdit(c.body ?? "");

      if (c.status === "scheduled" && c.scheduled_at) {
        setScheduleLocal(toDatetimeLocalValue(new Date(c.scheduled_at)));
      } else if (c.status === "draft") {
        if (!scheduleInitForDraft.current) {
          setScheduleLocal(toDatetimeLocalValue(new Date()));
          scheduleInitForDraft.current = true;
        }
      } else {
        setScheduleLocal("");
      }

      if (rRes.status === "fulfilled") {
        setRecipients(Array.isArray(rRes.value) ? rRes.value : []);
      } else {
        console.error(rRes.reason);
        setRecipients([]);
        setLoadError((prev) => prev ?? "Recipients list failed to load.");
      }
      if (insRes.status === "fulfilled") {
        setInsights(insRes.value?.insights || []);
      } else {
        console.error(insRes.reason);
        setInsights([]);
      }

      setAudienceDirty(false);
      if (c.status === "draft" || c.status === "scheduled") {
        try {
          const draft = await campaignAPI.draftAudience(id);
          setDraftGuests(draft.guests || []);
          setSelectedGuestIds(new Set((draft.guests || []).map((g) => g.id)));
        } catch {
          setDraftGuests([]);
          setSelectedGuestIds(new Set());
        }
      } else {
        setDraftGuests([]);
        setSelectedGuestIds(new Set());
      }
    } catch (e) {
      console.error(e);
      setLoadError("Could not load campaign.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const campaign = data?.campaign;

  const needsSave = (): boolean => {
    if (!campaign) return false;
    if (titleEdit !== (campaign.title ?? "") || bodyEdit !== (campaign.body ?? "")) return true;
    if (audienceDirty) return true;
    if (
      campaign.status === "scheduled" &&
      campaign.scheduled_at &&
      scheduleLocal.trim()
    ) {
      const a = new Date(scheduleLocal).getTime();
      const b = new Date(campaign.scheduled_at).getTime();
      if (!isNaN(a) && !isNaN(b) && Math.abs(a - b) > 60_000) return true;
    }
    return false;
  };

  const saveDraft = async (): Promise<boolean> => {
    if (!id || !campaign || !isAdmin) return true;
    if (!needsSave()) return true;
    if (audienceDirty && selectedGuestIds.size === 0) {
      alert("Select at least one guest, or clear audience changes.");
      return false;
    }
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        title: titleEdit,
        body: bodyEdit,
      };
      if (audienceDirty) {
        payload.audience_type = "manual";
        payload.manual_guest_ids = Array.from(selectedGuestIds);
      }
      if (campaign.status === "scheduled" && scheduleLocal.trim()) {
        payload.scheduled_at = new Date(scheduleLocal).toISOString();
      }
      await campaignAPI.update(id, payload);
      setAudienceDirty(false);
      await load();
      return true;
    } catch (e) {
      console.error(e);
      alert("Could not save changes.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!id || !campaign) return;
    const ok = await saveDraft();
    if (!ok) return;
    if (!window.confirm("Send this campaign to all selected guests?")) return;
    try {
      let body: { scheduled_at?: string } | undefined;
      if (scheduleLocal.trim()) {
        const when = new Date(scheduleLocal);
        if (!isNaN(when.getTime()) && when.getTime() > Date.now()) {
          body = { scheduled_at: when.toISOString() };
        }
      }
      await campaignAPI.publish(id, body);
      await load();
    } catch (e) {
      console.error(e);
      alert("Publish failed — check WhatsApp configuration.");
    }
  };

  const sendNow = async () => {
    if (!id || !window.confirm("Send this campaign now?")) return;
    const ok = await saveDraft();
    if (!ok) return;
    try {
      await campaignAPI.publish(id, {});
      await load();
    } catch (e) {
      console.error(e);
      alert("Publish failed — check WhatsApp configuration.");
    }
  };

  const toggleGuest = (guestId: string) => {
    setSelectedGuestIds((prev) => {
      const next = new Set(prev);
      if (next.has(guestId)) next.delete(guestId);
      else next.add(guestId);
      return next;
    });
    setAudienceDirty(true);
  };

  const selectAllGuests = () => {
    setSelectedGuestIds(new Set(draftGuests.map((g) => g.id)));
    setAudienceDirty(true);
  };

  const clearGuests = () => {
    setSelectedGuestIds(new Set());
    setAudienceDirty(true);
  };

  if (loading || !data || !campaign) {
    return (
      <div className="min-h-full flex items-center justify-center p-12 text-grey-guestby">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const { stats } = data;
  const isPreSend = campaign.status === "draft" || campaign.status === "scheduled";
  const showPipeline = !isPreSend || recipients.length > 0;

  return (
    <div className="min-h-full p-8 bg-slate-50">
      <div className="max-w-5xl mx-auto">
        <Link
          to="/campaigns"
          className="inline-flex items-center gap-1 text-sm text-grey-guestby hover:text-black-guestby mb-6"
        >
          <ArrowLeft className="h-4 w-4" /> Back to campaigns
        </Link>

        {loadError && (
          <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900">
            {loadError}
          </div>
        )}

        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div className="flex-1 min-w-[240px]">
            {isAdmin && isPreSend ? (
              <input
                className="text-2xl font-bold text-black-guestby w-full border border-slate-200 rounded-lg px-3 py-2 mb-2"
                value={titleEdit}
                onChange={(e) => setTitleEdit(e.target.value)}
                aria-label="Campaign title"
              />
            ) : (
              <h1 className="text-2xl font-bold text-black-guestby">{campaign.title}</h1>
            )}
            <p className="text-grey-guestby text-sm mt-1 capitalize">
              {campaign.status} · {campaign.audience_type?.replace(/_/g, " ")}
            </p>
            {isPreSend && audiencePreview && (
              <p className="text-sm text-slate-700 mt-2">
                <span className="font-medium text-black-guestby">
                  {audienceDirty ? selectedGuestIds.size : audiencePreview.eligible}
                </span>{" "}
                {audienceDirty ? "selected" : "eligible"} guest
                {(audienceDirty ? selectedGuestIds.size : audiencePreview.eligible) === 1
                  ? ""
                  : "s"}{" "}
                (checked-in, marketing opt-in
                {campaign.audience_type === "segment" ? ", matching segment rules" : ""}
                {campaign.audience_type === "manual" ? ", from your list" : ""}
                ).
                {audienceDirty && (
                  <span className="text-amber-800"> Save to apply audience changes.</span>
                )}
              </p>
            )}
          </div>
          {isAdmin && campaign.status === "draft" && (
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div>
                <label className="block text-xs text-grey-guestby mb-1">
                  Schedule send (defaults to now — pick a future time to schedule)
                </label>
                <input
                  type="datetime-local"
                  className="border border-slate-300 rounded-lg px-3 py-2 text-sm text-black-guestby"
                  value={scheduleLocal}
                  onChange={(e) => setScheduleLocal(e.target.value)}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={saving || !needsSave()}
                  onClick={() => saveDraft()}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-black-guestby font-medium disabled:opacity-50"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save changes
                </button>
                <button
                  type="button"
                  onClick={publish}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-guestby text-black-guestby font-medium disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                  {scheduleLocal.trim() &&
                  new Date(scheduleLocal).getTime() > Date.now()
                    ? "Schedule or send"
                    : "Publish & send"}
                </button>
              </div>
            </div>
          )}
          {isAdmin && campaign.status === "scheduled" && (
            <div className="flex flex-col gap-2 items-end">
              <div>
                <label className="block text-xs text-grey-guestby mb-1">
                  Scheduled send time (adjust if needed)
                </label>
                <input
                  type="datetime-local"
                  className="border border-slate-300 rounded-lg px-3 py-2 text-sm text-black-guestby"
                  value={scheduleLocal}
                  onChange={(e) => setScheduleLocal(e.target.value)}
                />
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                <button
                  type="button"
                  disabled={saving || !needsSave()}
                  onClick={() => saveDraft()}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-black-guestby font-medium disabled:opacity-50"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save changes
                </button>
                <button
                  type="button"
                  onClick={sendNow}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-guestby text-black-guestby font-medium"
                >
                  <Send className="h-4 w-4" />
                  Send now
                </button>
              </div>
            </div>
          )}
        </div>

        {isAdmin && isPreSend ? (
          <div className="mb-6">
            <label className="block text-xs font-medium text-grey-guestby mb-1">Message</label>
            <textarea
              className="w-full min-h-[140px] rounded-xl border border-slate-200 bg-white p-4 text-black-guestby text-sm whitespace-pre-wrap"
              value={bodyEdit}
              onChange={(e) => setBodyEdit(e.target.value)}
              aria-label="Campaign message body"
            />
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white p-4 mb-6 whitespace-pre-wrap text-black-guestby text-sm">
            {campaign.body}
          </div>
        )}

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" aria-label="Campaign statistics">
            {[
              ["Eligible", stats.eligible],
              ["Sent", stats.sent],
              ["Replied", stats.replied],
              ["No response", stats.no_response],
              ["Positive", stats.positive],
              ["Neutral", stats.neutral],
              ["Negative", stats.negative],
              [
                "Reply rate",
                `${((stats.reply_rate || 0) * 100).toFixed(0)}%`,
              ],
            ].map(([label, val]) => (
              <div
                key={String(label)}
                className="rounded-lg border border-slate-200 bg-white p-3"
              >
                <div className="text-xs text-grey-guestby uppercase">{label}</div>
                <div className="text-xl font-semibold text-black-guestby">{val}</div>
              </div>
            ))}
          </div>
        )}

        {insights.length > 0 && (
          <div className="rounded-xl border border-amber-200/80 bg-amber-50/80 p-4 mb-8">
            <h2 className="text-sm font-semibold text-black-guestby flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-amber-600" />
              Insights
            </h2>
            <ul className="list-disc list-inside text-sm text-slate-700 space-y-1">
              {insights.map((i: { message: string }, idx: number) => (
                <li key={idx}>{i.message}</li>
              ))}
            </ul>
          </div>
        )}

        <h2 className="text-lg font-semibold text-black-guestby mb-3">
          {isPreSend && recipients.length === 0 ? "Audience" : "Recipients"}
        </h2>
        {isPreSend && recipients.length === 0 && draftGuests.length === 0 && audiencePreview && audiencePreview.sample.length === 0 && (
          <p className="text-sm text-slate-600 mb-3">
            No matching guests right now. Adjust the segment, audience, or guest marketing opt-in, then
            save.
          </p>
        )}
        {isPreSend && recipients.length === 0 && isAdmin && draftGuests.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            <button
              type="button"
              onClick={selectAllGuests}
              className="text-xs font-medium text-blue-guestby hover:underline"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={clearGuests}
              className="text-xs font-medium text-slate-600 hover:underline"
            >
              Clear all
            </button>
          </div>
        )}
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="bg-black-guestby text-grey-guestby uppercase text-xs">
              <tr>
                {isPreSend && recipients.length === 0 && isAdmin && draftGuests.length > 0 && (
                  <th className="px-4 py-2 w-10">Include</th>
                )}
                <th className="px-4 py-2">Guest</th>
                <th className="px-4 py-2">Room</th>
                {showPipeline ? (
                  <>
                    <th className="px-4 py-2">Pipeline</th>
                    <th className="px-4 py-2">Sentiment</th>
                  </>
                ) : (
                  <th className="px-4 py-2" colSpan={2}>
                    Before send
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {recipients.length > 0
                ? recipients.map((row: any) => (
                    <tr key={row.id}>
                      <td className="px-4 py-2 text-black-guestby">{row.guest_name}</td>
                      <td className="px-4 py-2 text-grey-guestby">{row.room_number}</td>
                      <td className="px-4 py-2 capitalize text-grey-guestby">{row.pipeline_status}</td>
                      <td className="px-4 py-2 capitalize text-grey-guestby">{row.sentiment ?? "—"}</td>
                    </tr>
                  ))
                : isPreSend && draftGuests.length > 0
                  ? draftGuests.map((g) => (
                      <tr key={g.id}>
                        {isAdmin && (
                          <td className="px-4 py-2">
                            <input
                              type="checkbox"
                              checked={selectedGuestIds.has(g.id)}
                              onChange={() => toggleGuest(g.id)}
                              aria-label={`Include ${g.name}`}
                            />
                          </td>
                        )}
                        <td className="px-4 py-2 text-black-guestby">{g.name}</td>
                        <td className="px-4 py-2 text-grey-guestby">{g.room_number}</td>
                        {!showPipeline && (
                          <td className="px-4 py-2 text-slate-500 text-xs" colSpan={2}>
                            Included if checked when you save / publish.
                          </td>
                        )}
                      </tr>
                    ))
                  : isPreSend && audiencePreview && audiencePreview.sample.length > 0
                    ? audiencePreview.sample.map((s, i) => (
                        <tr key={`preview-${i}`}>
                          <td className="px-4 py-2 text-black-guestby">{s.name}</td>
                          <td className="px-4 py-2 text-grey-guestby">{s.room_number}</td>
                          <td className="px-4 py-2 text-slate-500 text-xs" colSpan={2}>
                            Preview sample — full list loads when available.
                          </td>
                        </tr>
                      ))
                    : null}
            </tbody>
          </table>
          {isPreSend &&
            recipients.length === 0 &&
            audiencePreview &&
            draftGuests.length === 0 &&
            audiencePreview.eligible > audiencePreview.sample.length && (
              <p className="px-4 py-2 text-xs text-slate-500 border-t border-slate-100">
                Showing {audiencePreview.sample.length} of {audiencePreview.eligible} eligible guests
                (preview).
              </p>
            )}
        </div>
      </div>
    </div>
  );
};

export default CampaignDetail;
